/**
 * SuperAdminOverview — Platform Overview landing page.
 *
 * iter309-redesign PR 2 shell. Aggregate cards only — clicking a
 * tenant-related tile opens the tenants page (later: deep-links into
 * the tenant detail with the right filter pre-applied).
 *
 * Backend aggregation endpoint (`/api/super-admin/overview`) will land
 * in a later PR. For now this page composes numbers from existing
 * endpoints on the client to unblock the shell.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Building2, Users, AlertTriangle, ShieldCheck, Activity,
  MailX, Globe, ScrollText, Loader2, Clock, CheckCircle2,
} from "lucide-react";

const API = (typeof window !== "undefined" && window.location
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL) + "/api";

const authHeader = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function Card({ icon: Icon, label, value, tone = "slate", to, tid, hint }) {
  const toneMap = {
    slate:   "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-100 text-emerald-700",
    amber:   "bg-amber-100 text-amber-700",
    rose:    "bg-rose-100 text-rose-700",
    sky:     "bg-sky-100 text-sky-700",
    violet:  "bg-violet-100 text-violet-700",
  };
  const body = (
    <div
      className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow transition-shadow"
      data-testid={tid}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
          <p className="mt-1 text-2xl font-semibold text-slate-900">
            {value ?? <span className="text-slate-400">—</span>}
          </p>
          {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
        </div>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${toneMap[tone] || toneMap.slate}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export default function SuperAdminOverview() {
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [userCounts, setUserCounts] = useState({});
  const [audit, setAudit] = useState([]);
  const [health, setHealth] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const safe = async (fn) => { try { return await fn(); } catch { return null; } };
      const [t, uc, al, h] = await Promise.all([
        safe(() => axios.get(`${API}/super-admin/tenants`, { headers: authHeader() })),
        safe(() => axios.get(`${API}/super-admin/tenants/user-counts`, { headers: authHeader() })),
        safe(() => axios.get(`${API}/super-admin/audit-log?limit=5`, { headers: authHeader() })),
        safe(() => axios.get(`${API}/super-admin/tenancy/health`, { headers: authHeader() })),
      ]);
      setTenants(t?.data?.tenants || t?.data || []);
      setUserCounts(uc?.data?.by_tenant || uc?.data || {});
      // Backend returns { audit_log: [...] } — normalize to a real array
      // so `.slice` on the render side never blows up when the shape
      // shifts across versions.
      const auditRaw = al?.data?.audit_log || al?.data?.items || al?.data;
      setAudit(Array.isArray(auditRaw) ? auditRaw : []);
      setHealth(h?.data || null);
      setLoading(false);
    })();
  }, []);

  const stats = (() => {
    const total = tenants.length;
    const byStatus = tenants.reduce((acc, t) => {
      const s = (t.subscription?.status || "unknown").toLowerCase();
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
    const missingSub = tenants.filter(t => !t.subscription?.plan_id).length;
    const totalActiveUsers = Object.values(userCounts).reduce((sum, e) => {
      const active = typeof e === "object" ? (e.active_users ?? e.active ?? 0) : 0;
      return sum + Number(active || 0);
    }, 0);
    return {
      total,
      active:      byStatus.active || 0,
      trial:       byStatus.trial || 0,
      past_due:    byStatus.past_due || 0,
      suspended:   byStatus.suspended || 0,
      missing_sub: missingSub,
      total_active_users: totalActiveUsers,
    };
  })();

  const isolation = (() => {
    if (!health) return { status: "unknown", label: "Not checked" };
    const collections_missing = (health.collections_missing_tenant_id || health.missing_tenant_ids || []).length;
    if (collections_missing === 0) return { status: "healthy", label: "All collections carry tenant_id" };
    return { status: "warning", label: `${collections_missing} collection(s) missing tenant_id` };
  })();

  return (
    <div className="space-y-6" data-testid="super-admin-overview-root">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Platform Overview</h1>
          <p className="text-sm text-slate-500 mt-1">
            Aggregate health of every tenant on this platform. Click a card to drill in.
          </p>
        </div>
        {loading && (
          <span className="text-xs text-slate-400 inline-flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </span>
        )}
      </header>

      {/* Row 1: Tenant volume */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
          Tenant volume
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Card tid="overview-total-tenants" icon={Building2} label="Total tenants" value={stats.total} to="/super-admin/tenants" />
          <Card tid="overview-active-tenants" icon={CheckCircle2} label="Active" value={stats.active} tone="emerald" to="/super-admin/tenants" />
          <Card tid="overview-trial-tenants" icon={Clock} label="Trial" value={stats.trial} tone="sky" to="/super-admin/tenants" />
          <Card tid="overview-past-due-tenants" icon={AlertTriangle} label="Past due" value={stats.past_due} tone="amber" to="/super-admin/tenants" />
          <Card tid="overview-suspended-tenants" icon={AlertTriangle} label="Suspended" value={stats.suspended} tone="rose" to="/super-admin/tenants" />
          <Card tid="overview-missing-sub" icon={AlertTriangle} label="Missing subscription" value={stats.missing_sub} tone={stats.missing_sub > 0 ? "rose" : "slate"} to="/super-admin/tenants" />
        </div>
      </section>

      {/* Row 2: Users + Health */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
          Users &amp; platform health
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Card tid="overview-active-users" icon={Users} label="Total active users" value={stats.total_active_users} tone="violet" />
          <Card
            tid="overview-isolation-health"
            icon={ShieldCheck}
            label="Tenant isolation"
            value={isolation.status === "healthy" ? "OK" : "!"}
            tone={isolation.status === "healthy" ? "emerald" : "amber"}
            hint={isolation.label}
          />
          <Card tid="overview-cron-health" icon={Activity} label="Cron health" value="—" hint="Coming in PR 6" />
          <Card tid="overview-domain-health" icon={Globe} label="Domain errors" value="—" hint="Coming in PR 4" />
          <Card tid="overview-billing-email-suppression" icon={MailX} label="Billing emails" value="—" hint="Coming in PR 6" />
          <Card tid="overview-incidents" icon={AlertTriangle} label="Open incidents" value="—" hint="Coming in PR 7" />
        </div>
      </section>

      {/* Recent super-admin activity */}
      <section className="bg-white border border-slate-200 rounded-lg" data-testid="overview-recent-audit">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-800 inline-flex items-center gap-2">
            <ScrollText className="w-4 h-4 text-slate-500" />
            Recent super-admin actions
          </h2>
          <Link to="/super-admin/audit-log" className="text-xs text-slate-500 hover:text-slate-900">
            View full log →
          </Link>
        </div>
        <div className="divide-y divide-slate-100">
          {(audit || []).slice(0, 5).map((row, i) => (
            <div key={row.id || i} className="px-4 py-3 flex items-start gap-3 text-sm">
              <div className="text-xs text-slate-400 w-32 flex-shrink-0 font-mono">
                {(row.created_at || row.timestamp || "").slice(0, 19).replace("T", " ")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 truncate">
                  {row.action || row.event_type || "action"}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  actor: <span className="font-mono">{row.actor_email || row.actor || "—"}</span>
                  {row.tenant_id ? <> · tenant: <span className="font-mono">{row.tenant_id}</span></> : null}
                </div>
              </div>
            </div>
          ))}
          {(!audit || audit.length === 0) && !loading && (
            <div className="px-4 py-6 text-sm text-slate-400 text-center">
              No recent super-admin actions found.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
